import React, { useState, useEffect } from 'react';

const PayrollCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [payrolls, setPayrolls] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [virtualDay, setVirtualDay] = useState(1);

  const statusFlow = ['Open', 'Awaiting Data', 'Data Submitted', 'Awaiting Approval', 'Approved', 'Concluded'];
  
  const statusColors = {
    'Open': 'bg-gray-600',
    'Awaiting Data': 'bg-orange-500',
    'Data Submitted': 'bg-blue-500',
    'Awaiting Approval': 'bg-yellow-500',
    'Rejected': 'bg-red-600',
    'Approved': 'bg-purple-500',
    'Concluded': 'bg-teal-500'
  };

  // Helper function to check if a day is a weekend
  const isWeekend = (day, month, year) => {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  };

  // Helper function to get the next business day
  const getNextBusinessDay = (day, month, year) => {
    let nextDay = day + 1;
    while (isWeekend(nextDay, month, year)) {
      nextDay++;
    }
    return nextDay;
  };

  // Generate sample payroll data with individual payrolls
  useEffect(() => {
    const generatePayrolls = () => {
      const newPayrolls = {};
      const daysInMonth = getDaysInMonth(currentMonth);
      const month = currentMonth.getMonth();
      const year = currentMonth.getFullYear();
      
      // Generate payrolls for the current month
      for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
        if (date.getMonth() === currentMonth.getMonth()) {
          const payrollsForDate = [];
          
          // Skip weekends for payroll generation
          if (!isWeekend(i, month, year)) {
            // Generate payrolls for all business days in the month
            // Assign 1-4 payrolls to most dates (80% chance)
            if (Math.random() > 0.2) {
              const numPayrolls = Math.floor(Math.random() * 4) + 1;
              for (let j = 0; j < numPayrolls; j++) {
                // RULE 1: No payroll can have Data Submitted or later until within 5 business days
                // RULE 2: All payrolls must be concluded at least 1 business day before deadline
                let initialStatus, initialStatusIndex;
                
                // Calculate how many business days ahead this payroll is from virtual day 1
                const businessDaysAhead = i - 1; // Since we start at virtual day 1
                
                if (businessDaysAhead <= 1) {
                  // Days 1-2: All should be concluded (Rule 2 - concluded before deadline)
                  initialStatus = 'Concluded';
                  initialStatusIndex = statusFlow.indexOf('Concluded');
                } else if (businessDaysAhead <= 5) {
                  // Days 3-6: Can have any status (within 5 business days - Rule 1)
                  const statusOptions = ['Concluded', 'Approved', 'Awaiting Approval', 'Data Submitted', 'Awaiting Data'];
                  const randomStatus = statusOptions[Math.floor(Math.random() * statusOptions.length)];
                  initialStatus = randomStatus;
                  initialStatusIndex = statusFlow.indexOf(randomStatus);
                } else {
                  // Days 7+: Only Open or Awaiting Data (Rule 1 - more than 5 business days)
                  const statusOptions = ['Open', 'Awaiting Data'];
                  const randomStatus = statusOptions[Math.floor(Math.random() * statusOptions.length)];
                  initialStatus = randomStatus;
                  initialStatusIndex = statusFlow.indexOf(randomStatus);
                }
                
                payrollsForDate.push({
                  id: `${i}-${j}`,
                  status: initialStatus,
                  statusIndex: initialStatusIndex,
                  deadline: i,
                  created: Date.now()
                });
              }
            }
          }
          
          if (payrollsForDate.length > 0) {
            newPayrolls[i] = payrollsForDate;
          }
        }
      }
      
      setPayrolls(newPayrolls);
      setCompletionPercentage(0);
      setVirtualDay(1);
    };

    generatePayrolls();
  }, [currentMonth]);

  // Animate payroll status progression
  useEffect(() => {
    let intervalId;
    let timeoutId;
    let currentVirtualDay = 1;
    
    const animatePayrolls = () => {
      setIsAnimating(true);
      let animationCount = 0;
      
      intervalId = setInterval(() => {
        // Progress virtual day (each 3 seconds = 1 day) - skip weekends
        const daysInMonth = getDaysInMonth(currentMonth);
        const month = currentMonth.getMonth();
        const year = currentMonth.getFullYear();
        
        // Find next business day
        let nextDay = currentVirtualDay + 1;
        while (nextDay <= daysInMonth && isWeekend(nextDay, month, year)) {
          nextDay++;
        }
        
        currentVirtualDay = nextDay > daysInMonth ? 1 : nextDay;
        setVirtualDay(currentVirtualDay);
        
        setPayrolls(prevPayrolls => {
          const updatedPayrolls = { ...prevPayrolls };
          let allConcluded = true;
          
          // Process payrolls based on current virtual day
          Object.keys(updatedPayrolls).forEach(date => {
            const dayNum = parseInt(date);
            updatedPayrolls[date] = updatedPayrolls[date].map(payroll => {
              if (payroll.status !== 'Concluded') {
                allConcluded = false;
                
                // RULE 1: No payroll can have Data Submitted or later until within 5 business days
                // RULE 2: All payrolls must be concluded at least 1 business day before deadline
                
                const businessDaysAhead = dayNum - currentVirtualDay;
                
                // RULE 1: Can only progress to Data Submitted+ if within 5 business days
                const canProgressToDataSubmitted = businessDaysAhead <= 5;
                
                // RULE 2: Must be concluded at least 1 day before deadline
                // If we're at or past (deadline - 1), this payroll MUST be concluded
                const mustBeConcluded = currentVirtualDay >= (dayNum - 1);
                
                if (mustBeConcluded) {
                  // Force to concluded if deadline rule applies
                  return {
                    ...payroll,
                    status: 'Concluded',
                    statusIndex: statusFlow.indexOf('Concluded'),
                    lastUpdated: Date.now()
                  };
                }
                
                // Determine if this payroll can progress based on current status and rules
                let progressChance = 0.0; // Default 0% chance
                
                if (payroll.status === 'Open') {
                  progressChance = 0.4; // 40% chance to move from Open
                } else if (payroll.status === 'Awaiting Data') {
                  // Can only progress to Data Submitted if within 5 business days (Rule 1)
                  progressChance = canProgressToDataSubmitted ? 0.6 : 0.1; // 60% if within 5 days, 10% if not
                } else if (payroll.status === 'Data Submitted') {
                  progressChance = 0.5; // 50% chance to move from Data Submitted
                } else if (payroll.status === 'Awaiting Approval') {
                  progressChance = 0.4; // 40% chance to move from Awaiting Approval
                } else if (payroll.status === 'Approved') {
                  progressChance = 0.7; // 70% chance to move from Approved to Concluded
                }
                
                // Apply progression chance
                const shouldProgress = Math.random() > (1 - progressChance);
                
                if (shouldProgress) {
                  const currentIndex = payroll.statusIndex;
                  const nextIndex = Math.min(currentIndex + 1, statusFlow.length - 1);
                  return {
                    ...payroll,
                    status: statusFlow[nextIndex],
                    statusIndex: nextIndex,
                    lastUpdated: Date.now()
                  };
                }
              }
              return payroll;
            });
          });
          
          // Calculate completion percentage
          const totalPayrolls = Object.values(updatedPayrolls).flat().length;
          const concludedPayrolls = Object.values(updatedPayrolls).flat().filter(p => p.status === 'Concluded').length;
          const percentage = totalPayrolls > 0 ? Math.round((concludedPayrolls / totalPayrolls) * 100) : 0;
          setCompletionPercentage(percentage);
          
          // Check if we've reached the last day of the month
          const daysInMonth = getDaysInMonth(currentMonth);
          if (currentVirtualDay >= daysInMonth) {
            // Move to next month immediately when last day is reached
            setCurrentMonth(prev => {
              const nextMonth = new Date(prev);
              nextMonth.setMonth(prev.getMonth() + 1);
              return nextMonth;
            });
          }
          
          return updatedPayrolls;
        });
        
        animationCount++;
      }, 3000); // 3 seconds = 1 virtual day
    };

    // Start animation after a short delay
    timeoutId = setTimeout(animatePayrolls, 1000);
    
    // Cleanup function to clear intervals when component unmounts or month changes
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentMonth]); // Only depend on currentMonth, not virtualDay

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getDaysArray = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return days;
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (day) => {
    return day === virtualDay;
  };

  const isSelected = (day) => {
    return selectedDate === day;
  };

  const getPayrollsForDate = (day) => {
    return payrolls[day] || [];
  };

  const getTotalPayrolls = () => {
    return Object.values(payrolls).flat().length;
  };

  return (
    <div className="bg-white rounded-lg shadow-2xl p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-2xl font-bold text-gray-900">{getMonthName(currentMonth)}</h3>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            {getTotalPayrolls()} payrolls found
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-600">
            Today: {virtualDay} {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-sm font-medium text-gray-900">
              Malta Sample: {completionPercentage}% Complete
            </div>
            <div className="w-24 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-teal-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-6">
        {/* Day headers */}
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
          <div key={day} className="p-3 text-center text-sm font-medium text-gray-500 bg-gray-50 rounded-lg">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {getDaysArray().map((day, index) => (
          <div
            key={index}
            className={`min-h-[80px] p-2 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 ${
              day ? 'hover:bg-blue-50 hover:border-blue-300' : 'bg-gray-50'
            } ${isToday(day) ? 'bg-blue-100 border-blue-300' : ''} ${
              isSelected(day) ? 'bg-blue-100 border-blue-500' : ''
            }`}
            onClick={() => day && setSelectedDate(selectedDate === day ? null : day)}
          >
            {day && (
              <>
                <div className="text-sm font-medium text-gray-900 mb-2">
                  {day}
                </div>
                <div className="flex flex-wrap gap-1">
                  {getPayrollsForDate(day).map((payroll, idx) => (
                    <div
                      key={idx}
                      className={`w-3 h-3 rounded-full ${statusColors[payroll.status]} transition-all duration-500`}
                      title={payroll.status}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Status Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Status Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(statusColors).filter(([status]) => !status.includes('FPS')).map(([status, color]) => (
            <div key={status} className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${color}`}></div>
              <span className="text-xs text-gray-700">{status}</span>
            </div>
          ))}
        </div>
      </div>


    </div>
  );
};

export default PayrollCalendar;
